// src/store/authContext.js (corrigido e seguro para frontend)
import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../supabase/supabaseClient';


export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pega sessão e dados do usuário na inicialização
  useEffect(() => {
    const getSessionAndUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      if (user?.id) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();

        const { data: perms } = await supabase
          .from('user_permissions')
          .select('permission')
          .eq('user_id', user.id);

        if (userData) setCurrentUser(userData);
        if (perms) setPermissions(perms.map(p => p.permission));
      }

      setLoading(false);
    };

    getSessionAndUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user;

        if (user?.id) {
          const { data: userData } = await supabase
            .from('users')
            .select('*')
            .eq('id', user.id)
            .single();

          const { data: perms } = await supabase
            .from('user_permissions')
            .select('permission')
            .eq('user_id', user.id);

          if (userData) setCurrentUser(userData);
          if (perms) setPermissions(perms.map(p => p.permission));
        } else {
          setCurrentUser(null);
          setPermissions([]);
        }
      }
    );

    return () => authListener?.subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!currentUser?.id) return;

      const { data, error } = await supabase
        .from('user_permissions')
        .select('permission')
        .eq('user_id', currentUser.id);

      if (error) {
        console.error('Erro ao buscar permissões:', error);
        setPermissions([]);
      } else {
        setPermissions(data.map(p => p.permission));
      }
    };

    fetchPermissions();
  }, [currentUser?.id]);

  const login = async (username, password) => {
  try {
    const { data: userRecord } = await supabase
      .from('users')
      .select('email')
      .eq('username', username)
      .single();

    console.log('🔍 Resultado da busca por username:', userRecord);

    if (!userRecord) return { success: false, message: 'Usuário não encontrado.' };

    const { error } = await supabase.auth.signInWithPassword({
      email: userRecord.email,
      password
    });

    if (error) return { success: false, message: 'Usuário ou senha incorretos.' };

    return { success: true };
  } catch (err) {
    console.error('Erro ao logar:', err);
    return { success: false, message: 'Erro ao logar. Tente novamente.' };
  }
};


  const logout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setPermissions([]);
  };

  const addUser = async ({ username, name, email, password, role = 'basic' }) => {
  try {
    // Verifica se já existe um username
    const { data: exists } = await supabase
      .from('users')
      .select('id')
      .eq('username', username)
      .single();

    if (exists) return { success: false, message: 'Usuário já existe.' };

    // Cadastra no Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password
    });

    if (authError) return { success: false, message: authError.message };

    const userId = authData?.user?.id;
    if (!userId) return { success: false, message: 'Erro ao obter ID do usuário criado.' };

    // Insere na tabela "users"
    const { error: insertError } = await supabase.from('users').insert({
      id: userId,
      username,
      name,
      email,
      role,
      created_at: new Date().toISOString()
    });

    if (insertError) return { success: false, message: insertError.message };

    // Define permissões padrão com base na role
    const defaultPermissions = {
      admin: ['view_products', 'edit_products', 'view_team', 'edit_team', 'view_finances', 'edit_finances', 'view_traffic', 'edit_traffic'],
      manager: ['view_products', 'edit_products', 'view_team', 'view_finances', 'view_traffic', 'edit_traffic'],
      marketing: ['view_products', 'view_traffic', 'edit_traffic'],
      basic: ['view_products', 'view_traffic']
    };

    const permissionsToAdd = defaultPermissions[role] || [];

    if (permissionsToAdd.length > 0) {
      await supabase.from('user_permissions').insert(
        permissionsToAdd.map((perm) => ({
          user_id: userId,
          permission: perm
        }))
      );
    }

    return { success: true };
  } catch (err) {
    console.error('Erro ao cadastrar novo usuário:', err);
    return { success: false, message: 'Erro inesperado ao cadastrar usuário.' };
  }
};

  const updateUser = async (id, data) => {
    try {
      if (data.password) {
        const { error: pwdError } = await supabase.auth.updateUser({
          password: data.password
        });
        if (pwdError) return { success: false, message: pwdError.message };
      }

      const { password, ...cleanData } = data;

      const { error: updateError } = await supabase
        .from('users')
        .update(cleanData)
        .eq('id', id);

      if (updateError) return { success: false, message: updateError.message };

      if (currentUser?.id === id) {
        const { data: updated } = await supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .single();

        if (updated) setCurrentUser(updated);
      }

      return { success: true };
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      return { success: false, message: 'Erro ao atualizar usuário.' };
    }
  };

  const hasPermission = (perm) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    return permissions.includes(perm);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        permissions,
        login,
        logout,
        addUser,
        updateUser,
        hasPermission,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
